/* VibeCanvas theme script (2026-10 rebuild). Vanilla, no build step.
   Every decorative effect is behind a switch in window.VC_FX (theme settings ›
   Motion & effects). Desktop-only effects check for a fine pointer; reduced
   motion turns all of them off and leaves the content fully visible. */
(() => {
  'use strict';

  const FX = Object.assign({
    smoothScroll: true, heroLight: true, reveal: true, splitHeadings: true,
    magnetic: true, cursorLight: true, galleryAmbient: true, marqueeVelocity: true
  }, window.VC_FX || {});
  const root = document.documentElement;
  const mq = (q) => window.matchMedia(q);
  const reduced = mq('(prefers-reduced-motion: reduce)');
  const finePointer = mq('(hover: hover) and (pointer: fine)');
  const wide = mq('(min-width: 1024px)');
  const isDesktop = () => finePointer.matches && wide.matches;
  const motionOK = () => !reduced.matches;
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const smooth = (a, b, v) => { const t = clamp((v - a) / (b - a)); return t * t * (3 - 2 * t); };
  const $ = (s, el = document) => el.querySelector(s);
  const $$ = (s, el = document) => Array.from(el.querySelectorAll(s));
  const header = () => $('[data-vc-header]');
  const headerOffset = () => (header() ? header().getBoundingClientRect().height : 0);

  /* ---------- Scroll state shared by effects ---------- */
  const scroll = { y: window.scrollY, velocity: 0, lenis: null, listeners: new Set() };
  const onScroll = (fn) => { scroll.listeners.add(fn); return () => scroll.listeners.delete(fn); };
  let lastY = window.scrollY;
  let scrollTicking = false;
  function emitScroll() {
    scrollTicking = false;
    scroll.y = window.scrollY;
    if (!scroll.lenis) { scroll.velocity = scroll.y - lastY; lastY = scroll.y; }
    scroll.listeners.forEach((fn) => fn(scroll));
  }
  window.addEventListener('scroll', () => {
    if (!scrollTicking) { scrollTicking = true; requestAnimationFrame(emitScroll); }
  }, { passive: true });

  function scrollToTarget(target, { offset = 0, immediate = false } = {}) {
    const y = typeof target === 'number' ? target : target.getBoundingClientRect().top + window.scrollY + offset;
    if (scroll.lenis && !immediate) {
      scroll.lenis.scrollTo(y, { duration: 1.6 });
    } else {
      window.scrollTo({ top: y, behavior: immediate || reduced.matches ? 'auto' : 'smooth' });
    }
  }

  /* ---------- Smooth scroll (desktop, mouse/trackpad only) ---------- */
  function initSmoothScroll() {
    if (!FX.smoothScroll || !isDesktop() || !motionOK() || !window.VC_ASSETS?.lenis) return;
    const script = document.createElement('script');
    script.src = window.VC_ASSETS.lenis;
    script.async = true;
    script.onload = () => {
      if (!window.Lenis || !isDesktop() || !motionOK()) return;
      const lenis = new window.Lenis({ lerp: 0.065, smoothWheel: true, autoRaf: true, anchors: false });
      scroll.lenis = lenis;
      window.__lenis = lenis;
      lenis.on('scroll', (l) => { scroll.velocity = l.velocity; });
      const stop = () => { lenis.destroy(); scroll.lenis = null; window.__lenis = undefined; };
      reduced.addEventListener('change', stop, { once: true });
      finePointer.addEventListener('change', stop, { once: true });
    };
    document.head.appendChild(script);
  }

  /* Same-page anchor links glide with Lenis and move focus to the target. */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="#"]');
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const url = new URL(link.href, location.href);
    if (url.pathname !== location.pathname || url.search !== location.search || !url.hash || url.hash === '#') return;
    const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;
    event.preventDefault();
    closeMenu();
    const isTop = link.hasAttribute('data-vc-to-top');
    scrollToTarget(isTop ? 0 : target, { offset: isTop ? 0 : -8 });
    history.pushState(null, '', url.hash);
    const focusLater = () => {
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    };
    setTimeout(focusLater, scroll.lenis ? 1650 : 600);
    if (isTop) document.dispatchEvent(new CustomEvent('vc:replay-hero'));
  });

  /* ---------- Header: scrolled state, hide on scroll down ---------- */
  function initHeader() {
    const el = header();
    if (!el) return;
    let prev = window.scrollY;
    onScroll(({ y }) => {
      el.classList.toggle('is-scrolled', y > 24);
      const goingDown = y > prev + 4;
      const goingUp = y < prev - 4;
      if (goingDown && y > 420 && !root.classList.contains('vc-menu-open')) el.classList.add('is-hidden');
      else if (goingUp || y < 120) el.classList.remove('is-hidden');
      prev = y;
    });
    el.classList.toggle('is-scrolled', window.scrollY > 24);
    el.addEventListener('focusin', () => el.classList.remove('is-hidden'));
  }

  /* ---------- Mobile menu ---------- */
  let menuReturnFocus = null;
  function closeMenu() {
    const menu = $('[data-vc-menu]');
    const toggle = $('[data-vc-menu-toggle]');
    if (!menu || !root.classList.contains('vc-menu-open')) return;
    root.classList.remove('vc-menu-open');
    menu.classList.remove('is-open');
    toggle?.setAttribute('aria-expanded', 'false');
    scroll.lenis?.start();
    setTimeout(() => { if (!root.classList.contains('vc-menu-open')) menu.hidden = true; }, 450);
    menuReturnFocus?.focus({ preventScroll: true });
  }
  function initMenu() {
    const menu = $('[data-vc-menu]');
    const toggle = $('[data-vc-menu-toggle]');
    if (!menu || !toggle) return;
    toggle.addEventListener('click', () => {
      if (root.classList.contains('vc-menu-open')) { closeMenu(); return; }
      menuReturnFocus = toggle;
      menu.hidden = false;
      requestAnimationFrame(() => {
        root.classList.add('vc-menu-open');
        menu.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        scroll.lenis?.stop();
        $('a', menu)?.focus({ preventScroll: true });
      });
    });
    menu.addEventListener('click', (e) => { if (e.target.closest('a')) closeMenu(); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeMenu();
      if (e.key !== 'Tab' || !root.classList.contains('vc-menu-open')) return;
      const items = [toggle, ...$$('a, button', menu)];
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    wide.addEventListener('change', (e) => { if (e.matches) closeMenu(); });
  }

  /* ---------- Reveal + split headings ---------- */
  function splitWords(el) {
    if (el.dataset.splitDone) return;
    el.dataset.splitDone = 'true';
    let index = 0;
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === 3) {
          const parts = child.textContent.split(/(\s+)/);
          const frag = document.createDocumentFragment();
          parts.forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            const span = document.createElement('span');
            span.className = 'vc-split-word';
            span.style.setProperty('--wi', index++);
            span.textContent = part;
            frag.appendChild(span);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1 && !child.classList.contains('vc-split-word')) {
          walk(child);
        }
      });
    };
    walk(el);
  }
  function initReveal() {
    const canReveal = FX.reveal && motionOK() && 'IntersectionObserver' in window;
    const canSplit = FX.splitHeadings && motionOK() && 'IntersectionObserver' in window;
    if (canReveal) root.classList.add('vc-fx-reveal');
    if (canSplit) root.classList.add('vc-fx-split');
    if (!canReveal && !canSplit) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px 30% 0px' });
    if (canReveal) $$('[data-reveal]').forEach((el) => io.observe(el));
    if (canSplit) $$('[data-split]').forEach((el) => { splitWords(el); io.observe(el); });
    reduced.addEventListener('change', () => {
      $$('[data-reveal], [data-split]').forEach((el) => el.classList.add('is-in'));
    });
  }

  /* ---------- Home hero: the canvas lights up (CSS 3D, no WebGL) ---------- */
  const COLORS = { cyan: [65, 207, 232], red: [241, 63, 55] };
  function sceneState(progress, mobile) {
    const p = clamp(progress);
    const pullback = smooth(0.04, 0.83, p);
    const turn = Math.sin(p * Math.PI);
    return {
      p,
      scale: 1 + (mobile ? 0.18 : 0.5) * (1 - pullback),
      rx: 0.08 * (1 - pullback),
      ry: -0.6 * (1 - pullback) + (mobile ? 0.5 : 0.88) * turn,
      rz: -0.105 * (1 - pullback) + 0.055 * turn,
      cyan: smooth(0.23, 0.49, p),
      red: smooth(0.57, 0.81, p),
      sweep: -0.3 + 1.9 * smooth(0, 0.3, p),
      sweepStrength: 0.82 * (1 - smooth(0.24, 0.36, p)),
      exposure: 0.47 + 0.5 * smooth(0.02, 0.33, p),
      reflection: 0.08 + 0.32 * smooth(0.16, 0.63, p)
    };
  }
  function initHero() {
    const hero = $('[data-vc-hero]');
    if (!hero) return;
    const pin = $('.vc-hero__pin', hero);
    const track = $('.vc-hero__track', hero);
    const face = $('.vc-hero__face', hero);
    const sceneEl = $('.vc-hero__scene', hero);
    const captions = $$('.vc-hero__caption', hero);
    captions.forEach(splitWords);
    let mode = '';
    let modeRevision = 0;
    let raf = 0;
    let visible = true;
    let timed = null;
    let lastP = -1;
    let faceW = 1, faceH = 1;
    const written = {};
    // Let the main photo paint before the hidden lighting views use bandwidth.
    const photoReady = (img) => new Promise((resolve) => {
      if (!img || img.complete) { resolve(); return; }
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    }).then(() => img && img.decode ? img.decode().catch(() => {}) : undefined);
    const lightingReady = photoReady($('.vc-hero__layer--off img', hero))
      .then(() => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve))))
      .then(() => Promise.all($$('.vc-hero__layer:not(.vc-hero__layer--off) img', hero).map((img) => {
        loadDeferred(img);
        return photoReady(img);
      })));
    // Only touch a custom property when its value really changes.
    const put = (name, value) => { if (written[name] !== value) { written[name] = value; hero.style.setProperty(name, value); } };
    const measure = () => { faceW = face.offsetWidth || 1; faceH = face.offsetHeight || 1; };

    function apply(p, opts = {}) {
      const s = sceneState(p, mode !== 'scroll');
      put('--p', s.p.toFixed(3));
      // Keep the mobile photo in place while the lights animate; desktop keeps 3D motion.
      put('--s', mode === 'scroll' ? s.scale.toFixed(4) : '1');
      put('--rx', mode === 'scroll' ? `${(-s.rx).toFixed(4)}rad` : '0rad');
      put('--ry', mode === 'scroll' ? `${s.ry.toFixed(4)}rad` : '0rad');
      put('--rz', mode === 'scroll' ? `${(-s.rz).toFixed(4)}rad` : '0rad');
      put('--cy', `${((s.cyan * 1.2 - 0.1) * 100).toFixed(1)}%`);
      put('--rd', `${((s.red * 1.2 - 0.1) * 100).toFixed(1)}%`);
      // Fully masked layers stay transparent so they never count as the page's largest paint.
      put('--cy-o', s.cyan > 0.001 ? '1' : '0');
      put('--rd-o', s.red > 0.001 ? '1' : '0');
      put('--shade', (1 - s.exposure).toFixed(3));
      put('--refl', s.reflection.toFixed(2));
      put('--spill', s.cyan.toFixed(2));
      put('--beam', (0.25 + s.cyan * 0.75).toFixed(2));
      put('--beam-shift', `${((s.p - 0.5) * 60).toFixed(0)}px`);
      put('--glow-rgb', COLORS.cyan.map((c, i) => Math.round(lerp(c, COLORS.red[i], s.red))).join(' '));
      put('--lit', clamp(s.cyan * 0.85 + s.red * 0.15).toFixed(2));
      // The sweep band travels along its normal (69deg gradient) across the face.
      const d = (s.sweep - 0.54) / 0.836;
      put('--sweep-x', `${(-faceW * 0.5 + d * 0.933 * faceW).toFixed(0)}px`);
      put('--sweep-y', `${(-faceH * 0.5 - d * 0.359 * faceH).toFixed(0)}px`);
      put('--sweep-o', (s.sweepStrength * 0.9).toFixed(2));
      if (!opts.skipCaptions) {
        const windows = [[0.015, 0.3], [0.36, 0.62], [0.68, 1.01]];
        captions.forEach((c, i) => {
          const [a, b] = windows[i] || [2, 3];
          c.classList.toggle('is-on', p >= a && p < b);
        });
      }
    }
    function flat() { put('--s', '1'); put('--rx', '0rad'); put('--ry', '0rad'); put('--rz', '0rad'); put('--sweep-o', '0'); }

    function scrollProgress() {
      const range = track.offsetHeight - pin.offsetHeight;
      if (range <= 0) return 1;
      return clamp(-track.getBoundingClientRect().top / range);
    }
    function onFrame() {
      raf = 0;
      const p = scrollProgress();
      if (Math.abs(p - lastP) < 0.0004) return;
      lastP = p;
      apply(p);
    }
    const schedule = () => { if (!raf && mode === 'scroll') raf = requestAnimationFrame(onFrame); };

    function stopTimed() {
      if (timed) { cancelAnimationFrame(timed.raf); timed = null; }
      hero.classList.remove('is-idle');
    }
    // Touch devices: light up once the photos are ready, then CSS keeps the
    // light gently breathing between red and cyan (no per-frame script).
    function playTimed() {
      stopTimed();
      const start = performance.now();
      const intro = 3600;
      timed = { raf: 0 };
      const tick = (now) => {
        if (!timed) return;
        const t = now - start;
        if (t >= intro) {
          apply(1, { skipCaptions: true });
          flat();
          captions.forEach((c, i) => c.classList.toggle('is-on', i === captions.length - 1));
          hero.classList.add('is-idle');
          timed = null;
          return;
        }
        apply(1 - Math.pow(1 - t / intro, 2.2));
        timed.raf = requestAnimationFrame(tick);
      };
      timed.raf = requestAnimationFrame(tick);
    }

    function setPerspective() {
      const h = (sceneEl && sceneEl.offsetHeight) || pin.offsetHeight || window.innerHeight;
      put('--persp', `${Math.round(h * 1.635)}px`);
    }

    function setMode() {
      const revision = ++modeRevision;
      stopTimed();
      const enabled = FX.heroLight && motionOK();
      const next = !enabled ? 'static' : isDesktop() ? 'scroll' : 'timed';
      mode = next;
      hero.classList.toggle('is-scroll', next === 'scroll');
      hero.classList.toggle('is-static', next === 'static');
      hero.classList.toggle('is-timed', next === 'timed');
      setPerspective();
      measure();
      lastP = -1;
      if (next === 'static') { apply(1, { skipCaptions: true }); flat(); return; }
      if (next === 'scroll') { onFrame(); return; }
      apply(0);
      lightingReady.then(() => { if (revision === modeRevision && mode === 'timed') playTimed(); });
    }

    onScroll(schedule);
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => { measure(); setPerspective(); lastP = -1; schedule(); }).observe(face);
    } else {
      window.addEventListener('resize', () => { measure(); setPerspective(); lastP = -1; schedule(); }, { passive: true });
    }
    [reduced, finePointer, wide].forEach((m) => m.addEventListener('change', setMode));
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        hero.classList.toggle('is-offscreen', !visible);
      }).observe(hero);
    }
    document.addEventListener('vc:replay-hero', () => {
      if (mode === 'timed') setTimeout(() => { if (mode === 'timed') setMode(); }, 400);
    });
    setMode();
    hero.classList.add('is-ready');
  }

  /* ---------- Home gallery: switch lights, load views lazily ---------- */
  const LIGHT_DWELL = 1150;
  function loadDeferred(img) {
    if (!img || !img.dataset.src) return;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    delete img.dataset.src;
    delete img.dataset.srcset;
  }
  function initGallery() {
    const section = $('[data-vc-gallery]');
    if (!section) return;
    const cards = $$('[data-vc-series]', section);
    const ambient = FX.galleryAmbient && motionOK();

    function select(card, index, { user = true } = {}) {
      const imgs = $$('.vc-series__frame img', card);
      const leds = $$('.vc-led', card);
      const img = imgs[index];
      if (!img) return;
      card.dataset.index = index;
      leds.forEach((led, i) => led.setAttribute('aria-pressed', i === index ? 'true' : 'false'));
      const led = leds[index];
      if (led) {
        card.style.setProperty('--glow-rgb', led.dataset.rgb);
        if (ambient && user) section.style.setProperty('--amb-rgb', led.dataset.rgb);
      }
      // Keep the current photo on screen until the next one is decoded.
      const show = () => { if (+card.dataset.index === index) imgs.forEach((im, i) => im.classList.toggle('is-active', i === index)); };
      loadDeferred(img);
      if (img.complete && img.naturalWidth) { show(); return; }
      const done = () => { clearTimeout(t); show(); };
      const t = setTimeout(show, 2500);
      (img.decode ? img.decode() : Promise.reject()).then(done, () => img.addEventListener('load', done, { once: true }));
    }

    cards.forEach((card) => {
      const leds = $$('.vc-led', card);
      leds.forEach((led, i) => {
        led.addEventListener('click', (e) => { e.preventDefault(); stopCycle(card); select(card, i); });
        const warm = () => loadDeferred($$('.vc-series__frame img', card)[i]);
        led.addEventListener('pointerenter', warm);
        led.addEventListener('focus', warm);
      });
      if (finePointer.matches && motionOK() && leds.length > 1) {
        const media = $('.vc-series__media', card);
        media.addEventListener('pointerenter', () => { $$('.vc-series__frame img', card).forEach(loadDeferred); startCycle(card); });
        media.addEventListener('pointerleave', () => stopCycle(card));
      }
      if (ambient) {
        card.addEventListener('pointerenter', () => {
          const rect = card.getBoundingClientRect();
          const srect = section.getBoundingClientRect();
          section.style.setProperty('--amb-x', `${((rect.left + rect.width / 2 - srect.left) / srect.width * 100).toFixed(1)}%`);
          section.style.setProperty('--amb-y', `${((rect.top + rect.height / 2 - srect.top) / srect.height * 100).toFixed(1)}%`);
          section.style.setProperty('--amb-rgb', getComputedStyle(card).getPropertyValue('--glow-rgb').trim() || '72 214 238');
        });
      }
    });

    function startCycle(card) {
      stopCycle(card);
      const imgs = $$('.vc-series__frame img', card);
      imgs.forEach(loadDeferred);
      card._cycle = setInterval(() => {
        const next = ((+card.dataset.index || 0) + 1) % imgs.length;
        select(card, next);
      }, LIGHT_DWELL);
    }
    function stopCycle(card) { clearInterval(card._cycle); card._cycle = null; }

    // Mobile rail progress bar.
    const rail = $('.vc-gallery__grid', section);
    const bar = $('.vc-rail-progress span', section);
    if (rail && bar) {
      const update = () => {
        const max = rail.scrollWidth - rail.clientWidth;
        const ratio = rail.clientWidth / rail.scrollWidth;
        bar.style.setProperty('--w', `${(ratio * 100).toFixed(1)}%`);
        const pos = max > 0 ? rail.scrollLeft / max : 0;
        bar.style.setProperty('--x', `${(pos * (1 / ratio - 1) * 100).toFixed(1)}%`);
      };
      rail.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    }
  }

  /* ---------- Home ideas: floating image follows the pointer ---------- */
  function initIdeas() {
    const list = $('[data-vc-ideas]');
    const float = $('[data-vc-ideas-float]');
    if (!list || !float) return;
    const imgs = $$('img', float);
    let warmed = false;
    const warm = () => { if (!warmed) { warmed = true; imgs.forEach(loadDeferred); } };
    list.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse' && isDesktop()) warm(); });
    let x = 0, y = 0, tx = 0, ty = 0, vx = 0, raf = 0, active = false;
    const loop = () => {
      raf = 0;
      x = lerp(x, tx, 0.16);
      y = lerp(y, ty, 0.16);
      vx = lerp(vx, tx - x, 0.2);
      float.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%) rotate(${clamp(vx * 0.06, -8, 8)}deg) scale(${active ? 1 : 0.86})`;
      if (active || Math.abs(tx - x) > 0.5) raf = requestAnimationFrame(loop);
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(loop); };
    list.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse' || !isDesktop()) return;
      tx = e.clientX; ty = e.clientY;
      if (!active) { x = tx; y = ty; }
      kick();
    });
    $$('.vc-idea', list).forEach((row, i) => {
      row.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || !isDesktop()) return;
        active = true;
        float.classList.add('is-on');
        imgs.forEach((im, j) => im.classList.toggle('is-active', j === i));
        kick();
      });
    });
    list.addEventListener('pointerleave', () => { active = false; float.classList.remove('is-on'); kick(); });
    onScroll(() => { if (active) { active = false; float.classList.remove('is-on'); } });
  }

  /* ---------- Steps: the line lights up as you scroll past ---------- */
  function initSteps() {
    $$('[data-vc-steps]').forEach((section) => {
      const list = $('.vc-steps__list', section);
      const line = $('.vc-steps__line', section);
      const steps = $$('.vc-step', section);
      if (!list || !line) return;
      let visible = true;
      let last = -1;
      let thresholds = [];
      // Read step positions once (and on resize), never right after a style write.
      const measure = () => {
        const h = list.offsetHeight || 1;
        thresholds = steps.map((step, i) => (wide.matches ? (i / Math.max(steps.length - 1, 1)) * 0.94 : step.offsetTop / h));
        last = -1;
      };
      const update = () => {
        if (!visible) return;
        const r = list.getBoundingClientRect();
        const vh = window.innerHeight;
        const p = reduced.matches ? 1 : !wide.matches
          ? clamp((vh * 0.62 - r.top) / Math.max(r.height, 1))
          : clamp((vh * 0.85 - r.top) / (vh * 0.55));
        if (Math.abs(p - last) < 0.002) return;
        last = p;
        line.style.setProperty('--progress', p.toFixed(3));
        steps.forEach((step, i) => step.classList.toggle('is-lit', p >= thresholds[i] - 0.001));
      };
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; if (visible) { measure(); update(); } }, { rootMargin: '20% 0px' }).observe(section);
      }
      onScroll(update);
      window.addEventListener('resize', () => { measure(); update(); }, { passive: true });
      measure();
      update();
    });
  }

  /* ---------- Pointer light (final CTA) + footer wordmark ---------- */
  function initPointerLights() {
    const targets = $$('[data-vc-cursor-light]');
    if (FX.cursorLight && motionOK()) {
      targets.forEach((el) => {
        let raf = 0, mx = 50, my = 50, tx = 50, ty = 50;
        const loop = () => {
          raf = 0;
          mx = lerp(mx, tx, 0.08);
          my = lerp(my, ty, 0.08);
          el.style.setProperty('--mx', `${mx.toFixed(2)}%`);
          el.style.setProperty('--my', `${my.toFixed(2)}%`);
          if (Math.abs(mx - tx) > 0.05 || Math.abs(my - ty) > 0.05) raf = requestAnimationFrame(loop);
        };
        el.addEventListener('pointermove', (e) => {
          if (e.pointerType !== 'mouse') return;
          const r = el.getBoundingClientRect();
          tx = ((e.clientX - r.left) / r.width) * 100;
          ty = ((e.clientY - r.top) / r.height) * 100;
          if (!raf) raf = requestAnimationFrame(loop);
        });
      });
    }
    const mark = $('[data-vc-wordmark]');
    if (mark && FX.cursorLight && finePointer.matches && motionOK()) {
      const footer = mark.closest('footer');
      footer.addEventListener('pointermove', (e) => {
        if (e.pointerType !== 'mouse') return;
        const r = mark.getBoundingClientRect();
        mark.style.setProperty('--mx', `${((e.clientX - r.left) / r.width * 100).toFixed(1)}%`);
        mark.style.setProperty('--my', `${((e.clientY - r.top) / r.height * 100).toFixed(1)}%`);
        mark.style.setProperty('--wm-o', '1');
      });
      footer.addEventListener('pointerleave', () => mark.style.setProperty('--wm-o', '0.55'));
    }
  }

  /* ---------- Magnetic buttons ---------- */
  function initMagnetic() {
    if (!FX.magnetic || !finePointer.matches || !motionOK()) return;
    $$('[data-magnetic]').forEach((btn) => {
      const inner = btn.querySelector(':scope > span');
      btn.addEventListener('pointermove', (e) => {
        if (e.pointerType !== 'mouse') return;
        const r = btn.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        btn.style.transition = 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1), background-color 0.3s, color 0.3s, border-color 0.3s, box-shadow 0.5s';
        btn.style.transform = `translate3d(${dx * 0.3}px, ${dy * 0.3}px, 0)`;
        if (inner) { inner.style.transition = 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)'; inner.style.transform = `translate3d(${dx * -0.12}px, ${dy * -0.12}px, 0)`; }
      });
      btn.addEventListener('pointerleave', () => {
        btn.style.transition = 'transform 1.2s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.3s, color 0.3s, border-color 0.3s, box-shadow 0.5s';
        btn.style.transform = '';
        if (inner) { inner.style.transition = 'transform 1.2s cubic-bezier(0.34, 1.56, 0.64, 1)'; inner.style.transform = ''; }
      });
    });
  }

  /* ---------- Marquee: speed and lean follow scroll velocity ---------- */
  function initMarquee() {
    $$('[data-vc-marquee]').forEach((el) => {
      const track = $('.vc-marquee__track', el);
      const skewEl = $('.vc-marquee__skew', el) || track;
      if (!track) return;
      if (!motionOK()) { track.style.animation = 'none'; return; }
      // Phones keep the plain CSS loop (compositor only); speed reacts to scrolling on computers.
      if (!FX.marqueeVelocity || !finePointer.matches || !track.getAnimations) return;
      const anims = track.getAnimations();
      let rate = 1, skew = 0, raf = 0, visible = false, lastRate = 1, lastSkew = 0;
      const loop = () => {
        raf = 0;
        if (!visible) return;
        const v = scroll.velocity || 0;
        const target = clamp(1 + Math.abs(v) * 0.09, 1, 7) * (v < -0.5 ? -1 : 1);
        rate = lerp(rate, target, 0.08);
        skew = lerp(skew, clamp(-v * 0.35, -6, 6), 0.08);
        if (Math.abs(rate - lastRate) > 0.01) { anims.forEach((a) => { a.playbackRate = rate; }); lastRate = rate; }
        if (Math.abs(skew - lastSkew) > 0.04) { skewEl.style.setProperty('--skew', `${skew.toFixed(2)}deg`); lastSkew = skew; }
        const settled = Math.abs(v) < 0.05 && Math.abs(rate - 1) < 0.01 && Math.abs(skew) < 0.04;
        if (settled) {
          if (lastRate !== 1) { anims.forEach((a) => { a.playbackRate = 1; }); lastRate = 1; }
          if (lastSkew !== 0) { skewEl.style.setProperty('--skew', '0deg'); lastSkew = 0; }
          rate = 1; skew = 0;
          return;
        }
        if (!scroll.lenis) scroll.velocity *= 0.9;
        raf = requestAnimationFrame(loop);
      };
      const wake = () => { if (visible && !raf) raf = requestAnimationFrame(loop); };
      onScroll(wake);
      new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; wake(); }).observe(el);
    });
  }

  /* ---------- Product page ---------- */
  function initProduct() {
    const page = $('[data-vc-product]');
    if (!page) return;
    const main = $('[data-vc-pdp-main]', page);
    const thumbs = $$('[data-vc-pdp-thumb]', page);
    const slides = $$('[data-vc-pdp-slide]', page);
    function show(index) {
      slides.forEach((s, i) => {
        s.classList.toggle('is-active', i === index);
        if (i === index) $$('img', s).forEach(loadDeferred);
      });
      thumbs.forEach((t, i) => t.setAttribute('aria-pressed', i === index ? 'true' : 'false'));
    }
    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => {
        show(i);
        if (main && !wide.matches) main.scrollTo({ left: main.clientWidth * i, behavior: reduced.matches ? 'auto' : 'smooth' });
      });
      thumb.addEventListener('pointerenter', () => { const s = slides[i]; s && $$('img', s).forEach(loadDeferred); });
    });
    if (main) {
      // Phone: the gallery is a swipeable rail; keep thumbs/dots in sync.
      main.addEventListener('scroll', () => {
        if (wide.matches) return;
        const i = Math.round(main.scrollLeft / Math.max(main.clientWidth, 1));
        thumbs.forEach((t, j) => t.setAttribute('aria-pressed', j === i ? 'true' : 'false'));
        slides.forEach((s, j) => { if (Math.abs(j - i) <= 1) $$('img', s).forEach(loadDeferred); });
      }, { passive: true });
    }

    const form = $('[data-vc-product-form]', page);
    // Uploadly writes `border-radius/padding !important` inline on its button,
    // which no stylesheet can override; align that one button with the theme.
    if (form && 'MutationObserver' in window) {
      const polish = () => $$('#photouploadapp .uploadcare--widget__button', form).forEach((b) => {
        if (b.dataset.vcPolished) return;
        b.dataset.vcPolished = 'true';
        b.style.setProperty('border-radius', '999px', 'important');
        b.style.setProperty('padding', '0 20px', 'important');
        b.style.setProperty('min-height', '42px', 'important');
        b.style.setProperty('font-weight', '600', 'important');
      });
      new MutationObserver(polish).observe(form, { childList: true, subtree: true });
      polish();
    }
    const priceEls = $$('[data-vc-product-price]', page);
    const buttons = $$('[data-vc-product-submit]', page);
    const sticky = $('[data-vc-sticky-atc]', page);
    function sync() {
      if (!form) return;
      const selected = form.querySelector('input[name="id"]:checked') || form.querySelector('input[name="id"]');
      if (!selected) return;
      if (selected.dataset.price) priceEls.forEach((el) => { el.textContent = selected.dataset.price; });
      const available = selected.dataset.available !== 'false' && !selected.disabled;
      buttons.forEach((b) => {
        b.disabled = !available;
        const label = b.querySelector('[data-label]') || b;
        label.textContent = available ? (b.dataset.addLabel || 'Add to Cart') : 'Sold Out';
      });
      const size = $('[data-vc-sticky-size]', page);
      if (size && selected.dataset.title) size.textContent = selected.dataset.title;
    }
    form?.addEventListener('change', sync);
    sync();

    if (sticky && form) {
      // Checked on every scroll (not IntersectionObserver): a jump past the
      // button (anchor link, restored scroll position) must still update it.
      const target = $('[data-vc-product-submit]', form) || form;
      const footer = $('[data-vc-footer]');
      const render = () => {
        const pastButton = target.getBoundingClientRect().bottom < 0;
        const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
        const show = pastButton && !footerVisible;
        if (show === sticky.classList.contains('is-visible')) return;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        $$('a, button', sticky).forEach((el) => { el.tabIndex = show ? 0 : -1; });
      };
      onScroll(render);
      window.addEventListener('resize', render, { passive: true });
      render();
      $('[data-vc-sticky-buy]', sticky)?.addEventListener('click', () => {
        const btn = $('[data-vc-product-submit]', form);
        if (btn && !btn.disabled) {
          if (typeof form.requestSubmit === 'function') form.requestSubmit(btn); else btn.click();
        }
      });
      $('[data-vc-sticky-size-btn]', sticky)?.addEventListener('click', () => {
        scrollToTarget(form, { offset: -(headerOffset() + 24) });
      });
    }
  }

  /* ---------- <details> accordions open and close smoothly ---------- */
  function initAccordions() {
    $$('details[data-vc-accordion]').forEach((details) => {
      const summary = $('summary', details);
      const body = $('.vc-accordion__body', details);
      if (!summary || !body) return;
      summary.addEventListener('click', (e) => {
        if (reduced.matches || !body.animate) return;
        e.preventDefault();
        if (details.open) {
          const anim = body.animate([{ height: `${body.offsetHeight}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 320, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' });
          anim.onfinish = () => { details.open = false; };
        } else {
          details.open = true;
          const h = body.offsetHeight;
          body.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], { duration: 420, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' });
        }
      });
    });
  }

  /* ---------- Cart quantity steppers ---------- */
  function initCart() {
    $$('[data-vc-qty]').forEach((wrap) => {
      const input = $('input', wrap);
      $$('[data-step]', wrap).forEach((b) => b.addEventListener('click', () => {
        const next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(b.dataset.step, 10));
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        wrap.closest('form')?.querySelector('[data-vc-cart-update]')?.classList.add('is-dirty');
      }));
    });
  }

  /* ---------- Article reading progress ---------- */
  function initReading() {
    const bar = $('[data-vc-reading]');
    const article = $('[data-vc-article-body]');
    if (!bar || !article) return;
    const update = () => {
      const r = article.getBoundingClientRect();
      const p = clamp((window.innerHeight * 0.3 - r.top) / Math.max(r.height - window.innerHeight * 0.4, 1));
      bar.style.transform = `scaleX(${p.toFixed(4)})`;
    };
    onScroll(update);
    update();
  }

  function init() {
    initHeader();
    initMenu();
    initReveal();
    initHero();
    initGallery();
    initIdeas();
    initSteps();
    initPointerLights();
    initMagnetic();
    initMarquee();
    initProduct();
    initAccordions();
    initCart();
    initReading();
    initSmoothScroll();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
